import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import MoneyView from "components/common/views/MoneyView";
import { graph } from "config/axios-context";
import {
    RoomTypeFilterInput,
    RoomTypesDatalistQuery,
    RoomTypesDatalistQueryVariables,
    RoomTypeSortInput,
    SortEnumType
} from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import RoomTypeView from "./RoomTypeView";
import { uuid } from "../../../../config/app-constants";
import { Roles } from "utilities/app-utilities";
import { AppRoutes } from "config/router";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";

type DataItem = NonNullable<RoomTypesDatalistQuery['roomTypes']>['items'][0];

type RoomTypeDatalistPropsType = {
    name: string;
}

const query = gql`
query RoomTypesDatalist($where: RoomTypeFilterInput, $order: [RoomTypeSortInput!], $skip: Int, $take: Int) {
    roomTypes {
      count(where: $where)
      items(order: $order, skip: $skip, take: $take, where: $where) {
        id
        name
        defaultPrice
        color,
        rooms {
          count
        }
      }
    }
  }
`;

const deleteMutation = gql`
mutation DeleteRoomType($id: Int) {
    roomTypeDelete(id: $id)
  }
  `;

const RoomTypeDatalist = (props: RoomTypeDatalistPropsType) => {
    const context = useAppContext();

    const datalistConfig: DatalistPropsType<DataItem, RoomTypeFilterInput, RoomTypeSortInput> = {
        dataSource: {
            request: async params => {
                let variables: RoomTypesDatalistQueryVariables = {
                    where: params.filters,
                    order: params.sort,
                    skip: params.skip,
                    take: params.take
                };

                if (params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        name: {
                            contains: params.searchText
                        }
                    });
                }

                try {
                    const response = await graph<RoomTypesDatalistQuery, RoomTypesDatalistQueryVariables>({
                        query,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    return response.data!.roomTypes!;
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    id: uuid(),
                    name: 'name',
                    direction: SortEnumType.Asc
                }
            ]
        },
        itemId(i) {
            return i.id;
        },
        name: props.name + "-room-type-datalist",
        page: "off",
        table: {
            columns: [
                {
                    name: 'id',
                    card: {
                        icon: faHashtag,
                        group: 'id'
                    }
                },
                {
                    name: 'name',
                    card: {
                        group: 'id',
                        resolve: i => <RoomTypeView {...i} />,
                    }
                },
                {
                    name: 'defaultPrice',
                    resolve: i => <MoneyView value={i.defaultPrice} />,
                    card: {
                        group: 'id'
                    }
                },
                {
                    name: 'color',
                    resolve: i => i.color ? <RoomTypeView name={i.color} color={i.color} /> : <></>,
                    card: {
                        hide: true
                    }
                },
                {
                    name: 'rooms',
                    title: 'accommodation.roomQuantity',
                    resolve: i => i.rooms.count,
                }
            ],
            render: {
                actions: i => ({
                    mutationPermissionRole: context.userHasRole(Roles.ADMIN),
                    editUrl: AppRoutes.ACCOMMODATION_ROOM_TYPE_EDIT,
                    deleteMutation: i.rooms.count > 0 ? undefined : deleteMutation,
                })
            }
        }
    }

    return (
        <Datalist {...datalistConfig} />
    );
};

export default RoomTypeDatalist;
